import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import {
  FaSatelliteDish,
  FaNewspaper,
  FaUserFriends,
  FaBookOpen,
  FaBrain,
  FaPlayCircle,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSyncAlt,
  FaExternalLinkAlt,
  FaCalendarAlt,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechBar,
  TechBarFill,
  TechChip,
  TechButton,
  TechButtonGhost,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatDelta,
  StatSub,
  StatusPill,
  Grid,
  Span,
  Loading,
  Empty,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  listCompetitors,
  getCompetitorStats,
  listScrapeRuns,
  listRecentNews,
} from '../../services/competitorsService';
import { listResearch, listInsights } from '../../services/researchService';

/* ---------- chart tokens ---------- */

const tickStyle = { fontSize: 11, fontFamily: mono, fill: 'var(--text-disabled)' };
const tooltipStyle = {
  background: 'rgba(11, 19, 32, 0.95)',
  border: '1px solid rgba(0, 180, 216, 0.4)',
  borderRadius: 4,
  fontFamily: mono,
  fontSize: 12,
  color: 'var(--text-primary)',
};
const legendStyle = {
  paddingTop: 10,
  fontFamily: mono,
  fontSize: 11,
  letterSpacing: '0.04em',
};
const palette = {
  candidato: '#00B4D8',
  alt1: '#06D6A0',
  alt2: '#FFD166',
  alt3: '#A4B0C2',
  alt4: '#7C8AA0',
  danger: '#EF476F',
};

/* ---------- styled ---------- */

const NewsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  counter-reset: nl;
`;

const NewsItem = styled.li`
  display: grid;
  grid-template-columns: 26px 1fr auto;
  gap: 0.55rem;
  padding: 0.55rem 0.7rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  counter-increment: nl;
  font-size: 0.85rem;
  transition: border-color 0.18s ease, transform 0.18s ease, background 0.18s ease;

  &::before {
    content: counter(nl, decimal-leading-zero);
    font-family: ${mono};
    font-size: 0.66rem;
    color: ${accent};
    letter-spacing: 0.06em;
    align-self: center;
  }

  a {
    color: var(--text-primary);
    text-decoration: none;
    line-height: 1.35;
    display: inline-flex;
    align-items: flex-start;
    gap: 6px;
    overflow: hidden;
    text-overflow: ellipsis;

    svg {
      color: var(--text-disabled);
      font-size: 0.7rem;
      margin-top: 4px;
      flex-shrink: 0;
    }

    &:hover { color: ${accent}; }
  }

  .meta {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    align-self: center;
    white-space: nowrap;
  }

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.04);
  }
`;

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SourceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 60px;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;

  .name { color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .count {
    text-align: right;
    font-family: ${mono};
    font-size: 0.75rem;
    color: ${accent};
  }
`;

const RunsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Th = styled.th`
  padding: 0.55rem 0.85rem;
  text-align: left;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-weight: 500;
  background: rgba(255, 255, 255, 0.025);
  border-bottom: 1px solid ${borderSoft};
  white-space: nowrap;
`;
const Td = styled.td`
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid ${borderSoft};
  color: var(--text-secondary);
  font-size: 0.84rem;
  font-family: ${({ $mono }) => ($mono ? mono : 'inherit')};
`;
const Tr = styled.tr`
  &:hover { background: rgba(255, 255, 255, 0.02); }
  &:last-child td { border-bottom: none; }
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 3px;
  border: 1px solid;
  ${({ status }) => {
    if (status === 'ok') return `color: var(--alert-success); border-color: rgba(6,214,160,0.45); background: rgba(6,214,160,0.08);`;
    if (status === 'partial') return `color: var(--alert-warning); border-color: rgba(255,209,102,0.45); background: rgba(255,209,102,0.08);`;
    if (status === 'error') return `color: var(--alert-error); border-color: rgba(239,71,111,0.45); background: rgba(239,71,111,0.08);`;
    return `color: ${accent}; border-color: rgba(0,180,216,0.4); background: rgba(0,180,216,0.08);`;
  }}
`;

const InsightCard = styled.article`
  position: relative;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${({ $color }) => $color || accent};
  border-radius: 6px;
  padding: 0.95rem 1.1rem;
  margin-bottom: 0.85rem;

  &:last-child { margin-bottom: 0; }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.45rem;

    .kind {
      font-family: ${mono};
      font-size: 0.65rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: ${({ $color }) => $color || accent};
    }
    .ts {
      margin-left: auto;
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
  }

  .body {
    color: var(--text-secondary);
    font-size: 0.88rem;
    line-height: 1.5;

    h2, h3, h4 { color: var(--text-primary); margin: 0.7em 0 0.3em; font-weight: 600; }
    h3 { font-size: 0.92em; text-transform: uppercase; letter-spacing: 0.08em; color: ${accent}; }
    p { margin: 0 0 0.55em; }
    ul, ol { margin: 0 0 0.55em 1.2em; padding: 0; }
    li { margin-bottom: 0.2em; }
    strong { color: var(--text-primary); }
    code {
      font-family: ${mono};
      background: rgba(0, 0, 0, 0.4);
      padding: 1px 5px;
      border-radius: 3px;
      color: #cfe9f4;
    }
  }
`;

const SectionFooter = ({ source, calc, last }) => (
  <div
    style={{
      marginTop: 10,
      paddingTop: 8,
      borderTop: `1px dashed ${borderSoft}`,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      fontFamily: mono,
      fontSize: '0.6rem',
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--text-disabled)',
    }}
  >
    <FaInfoCircle style={{ color: accent, fontSize: '0.75rem' }} />
    <span>Fuente: {source}</span>
    {calc && <><span>·</span><span>cálculo: {calc}</span></>}
    {last && <span style={{ marginLeft: 'auto' }}>act. {last}</span>}
  </div>
);

/* ---------- helpers ---------- */

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  const days = h / 24;
  if (days < 7) return `hace ${Math.round(days)}d`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const durationLabel = (start, end) => {
  if (!start || !end) return '—';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

/* ---------- componente principal ---------- */

const KPIs = () => {
  const [state, setState] = useState({ loading: true, error: null });
  const [data, setData] = useState({
    competitors: [],
    competitorStats: {},
    news: [],
    research: { items: [], runs: [], schedule: null },
    insights: { insights: [], schedule: null },
    scrape: { runs: [], totals: {}, schedule: null },
  });

  const load = async () => {
    setState({ loading: true, error: null });
    try {
      const [competitors, news, research, insights, scrape] = await Promise.all([
        listCompetitors().catch(() => []),
        listRecentNews({ limit: 100 }).catch(() => ({ news: [] })),
        listResearch(20).catch(() => ({ items: [], runs: [], schedule: null })),
        listInsights().catch(() => ({ insights: [], schedule: null })),
        listScrapeRuns(20).catch(() => ({ runs: [], totals: {}, schedule: null })),
      ]);

      // stats per competitor (count news in last 7d/30d)
      const competitorStats = {};
      const statsResults = await Promise.all(
        (competitors || []).map((c) => getCompetitorStats(c.slug).catch(() => null))
      );
      (competitors || []).forEach((c, i) => {
        if (statsResults[i]) competitorStats[c.slug] = statsResults[i];
      });

      setData({
        competitors: competitors || [],
        competitorStats,
        news: news.news || [],
        research,
        insights,
        scrape,
      });
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: friendlyError(err, 'No se pudieron cargar los datos.') });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ---- métricas derivadas (todas reales) ----
  const totals = data.scrape?.totals || {};
  const lastRun = data.scrape?.runs?.[0];
  const nextScrape = data.scrape?.schedule?.next_run_at;
  const nextResearch = data.insights?.schedule?.next_run_at;

  const newsByCompetitor = useMemo(() => {
    const map = {};
    for (const n of data.news) {
      const k = n.competitor_name || n.competitor_slug || 'Otros';
      map[k] = (map[k] || 0) + 1;
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .map(([name, count]) => ({ name, count }));
  }, [data.news]);

  const newsByDay = useMemo(() => {
    const map = new Map();
    for (const n of data.news) {
      const ts = n.published_at || n.fetched_at;
      if (!ts) continue;
      const d = new Date(ts);
      if (isNaN(d)) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(-30)
      .map(([key, value]) => {
        const d = new Date(key);
        return {
          name: d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }),
          value,
        };
      });
  }, [data.news]);

  const topSources = useMemo(() => {
    const map = {};
    for (const n of data.news) {
      const s = n.source || 'sin fuente';
      map[s] = (map[s] || 0) + 1;
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8);
  }, [data.news]);

  const researchByMonth = useMemo(() => {
    const map = new Map();
    for (const it of (data.research?.items || [])) {
      const ts = it.generated_at || it.date;
      if (!ts) continue;
      const d = new Date(ts);
      if (isNaN(d)) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([key, value]) => ({
        name: new Date(key + '-01').toLocaleDateString('es-AR', { month: 'short', year: '2-digit' }),
        value,
      }));
  }, [data.research]);

  const researchAuthors = useMemo(() => {
    const map = {};
    for (const it of (data.research?.items || [])) {
      map[it.author] = (map[it.author] || 0) + 1;
    }
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [data.research]);

  const totalArticlesSeen = data.news.length;
  const totalArticlesIndexed = totals.articles ?? data.news.length;
  const totalCompetitors = totals.competitors ?? data.competitors.length;
  const totalResearch = data.research?.items?.length || 0;
  const totalInsights = data.insights?.insights?.length || 0;

  if (state.loading) {
    return (
      <TechShell>
        <TechPageHeader
          title="KPIs · datos en vivo"
          subtitle="Métricas consolidadas desde los sistemas reales de la plataforma"
          dossier="KPI://LIVE"
        />
        <Loading>Cargando datos reales</Loading>
      </TechShell>
    );
  }

  if (state.error) {
    return (
      <TechShell>
        <TechPageHeader
          title="KPIs · datos en vivo"
          dossier="KPI://LIVE"
          actions={<TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Reintentar</TechButtonGhost>}
        />
        <ErrorBox>No se pudieron cargar los datos · {state.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="KPIs · datos en vivo"
        subtitle="Todos los números salen de lo que la plataforma mide de verdad. Nada está inventado."
        dossier="KPI://LIVE"
        actions={<TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>}
      >
        <StatusPill>Artículos indexados <strong>{totalArticlesIndexed}</strong></StatusPill>
        <StatusPill>Competidores <strong>{totalCompetitors}</strong></StatusPill>
        <StatusPill>Research items <strong>{totalResearch}</strong></StatusPill>
        <StatusPill>Insights IA <strong>{totalInsights}</strong></StatusPill>
        <StatusPill>Última corrida <strong>{fmtRel(lastRun?.started_at)}</strong></StatusPill>
      </TechPageHeader>

      {/* ============== Pipeline ============== */}
      <TechSection title="Monitoreo automático · estado en vivo" style={{ marginBottom: '1.25rem' }}>
        <StatStrip min="170px">
          <StatTile>
            <StatLabel><FaSatelliteDish /> Última corrida</StatLabel>
            <StatValue style={{ fontSize: '0.95rem' }}>{fmtDateTime(lastRun?.started_at)}</StatValue>
            {lastRun?.status && (
              <StatSub><StatusChip status={lastRun.status}>{lastRun.status === 'ok' ? <FaCheckCircle /> : <FaExclamationTriangle />} {lastRun.status}</StatusChip></StatSub>
            )}
          </StatTile>
          <StatTile>
            <StatLabel><FaClock /> Próxima corrida</StatLabel>
            <StatValue style={{ fontSize: '0.95rem' }}>{fmtDateTime(nextScrape)} UTC</StatValue>
            <StatSub>{data.scrape?.schedule?.cron || '—'}</StatSub>
          </StatTile>
          <StatTile>
            <StatLabel><FaPlayCircle /> Notas última corrida</StatLabel>
            <StatValue>+{lastRun?.articles_added ?? 0}</StatValue>
            <StatSub>{lastRun?.competitors_processed ?? 0} competidores procesados</StatSub>
          </StatTile>
          <StatTile>
            <StatLabel><FaBrain /> Próxima IA semanal</StatLabel>
            <StatValue style={{ fontSize: '0.95rem' }}>{fmtDateTime(nextResearch)} UTC</StatValue>
            <StatSub>{data.insights?.schedule?.cadence || 'weekly'}</StatSub>
          </StatTile>
        </StatStrip>

        <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
          <RunsTable>
            <thead>
              <tr>
                <Th>Run</Th><Th>Inicio</Th><Th>Duración</Th><Th>Status</Th>
                <Th>Procesados</Th><Th>Agregados</Th><Th>Trigger</Th>
              </tr>
            </thead>
            <tbody>
              {(data.scrape?.runs || []).slice(0, 5).map((r) => (
                <Tr key={r.id}>
                  <Td $mono>#{r.id}</Td>
                  <Td $mono>{fmtDateTime(r.started_at)}</Td>
                  <Td $mono>{durationLabel(r.started_at, r.finished_at)}</Td>
                  <Td><StatusChip status={r.status}>{r.status}</StatusChip></Td>
                  <Td $mono>{r.competitors_processed}</Td>
                  <Td $mono style={{ color: r.articles_added > 0 ? accent : 'var(--text-disabled)' }}>+{r.articles_added}</Td>
                  <Td $mono>{r.triggered_by || '—'}</Td>
                </Tr>
              ))}
              {(!data.scrape?.runs || data.scrape.runs.length === 0) && (
                <Tr><Td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-disabled)' }}>Sin corridas registradas</Td></Tr>
              )}
            </tbody>
          </RunsTable>
        </div>
        <SectionFooter source="monitoreo automático de la plataforma" calc="registro de cada barrido diario" last={fmtRel(lastRun?.started_at)} />
      </TechSection>

      {/* ============== Cobertura mediática ============== */}
      <Grid style={{ marginBottom: '1.25rem' }}>
        <Span span={7} className="mobile-full-width">
          <TechSection title={`Cobertura mediática por competidor · ${newsByCompetitor.length}`}>
            {newsByCompetitor.length === 0 ? (
              <Empty>No hay noticias indexadas todavía.</Empty>
            ) : (
              <div style={{ height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={newsByCompetitor} layout="vertical" margin={{ top: 5, right: 24, left: 110, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" stroke="var(--text-disabled)" tick={tickStyle} width={130} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0, 180, 216, 0.06)' }} />
                    <Bar dataKey="count" name="Notas">
                      {newsByCompetitor.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? palette.candidato : i === 1 ? palette.alt1 : i === 2 ? palette.alt2 : palette.alt3} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <SectionFooter source="noticias de competidores" calc="cantidad de notas por competidor en el período" last={fmtRel(lastRun?.started_at)} />
          </TechSection>
        </Span>

        <Span span={5} className="mobile-full-width">
          <TechSection title={`Top fuentes · ${topSources.length}`}>
            {topSources.length === 0 ? (
              <Empty>Sin datos.</Empty>
            ) : (
              <SourceList>
                {topSources.map(([name, count], i) => (
                  <div key={name}>
                    <SourceRow>
                      <span className="name">{name}</span>
                      <TechBar>
                        <TechBarFill
                          value={Math.round((count / topSources[0][1]) * 100)}
                          color={i === 0 ? palette.candidato : i < 3 ? palette.alt1 : palette.alt3}
                        />
                      </TechBar>
                      <span className="count">×{count}</span>
                    </SourceRow>
                  </div>
                ))}
              </SourceList>
            )}
            <SectionFooter source="competitor_news.source" calc="ranking por count(*) últimas 100 notas" last={fmtRel(lastRun?.started_at)} />
          </TechSection>
        </Span>
      </Grid>

      {/* ============== Volumen mensual ============== */}
      <TechSection title={`Volumen de noticias indexadas · últimos ${newsByDay.length} días con actividad`} style={{ marginBottom: '1.25rem' }}>
        {newsByDay.length === 0 ? (
          <Empty>Sin actividad reciente.</Empty>
        ) : (
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={newsByDay} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-news" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
                <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="value" name="Notas indexadas" stroke={accent} strokeWidth={2.4} fill="url(#g-news)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <SectionFooter source="competitor_news" calc="count(*) agrupado por día (publicación o fetch)" last={fmtRel(lastRun?.started_at)} />
      </TechSection>

      {/* ============== Competidores monitoreados ============== */}
      <TechSection title={`Competidores monitoreados · ${data.competitors.length}`} style={{ marginBottom: '1.25rem' }}>
        {data.competitors.length === 0 ? (
          <Empty>Sin competidores cargados.</Empty>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <RunsTable>
              <thead>
                <tr>
                  <Th>Competidor</Th>
                  <Th>Región</Th>
                  <Th>Líderes</Th>
                  <Th>Total noticias</Th>
                  <Th>Últimos 7d</Th>
                  <Th>Última actualización</Th>
                </tr>
              </thead>
              <tbody>
                {data.competitors.map((c) => {
                  const s = data.competitorStats[c.slug]?.stats;
                  return (
                    <Tr key={c.id || c.slug}>
                      <Td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{c.name}</Td>
                      <Td $mono>{c.region}</Td>
                      <Td>{c.leaders || '—'}</Td>
                      <Td $mono style={{ color: accent }}>{s?.total ?? '—'}</Td>
                      <Td $mono>{s?.last_7d ?? '—'}</Td>
                      <Td $mono>{fmtRel(c.last_scraped_at)}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </RunsTable>
          </div>
        )}
        <SectionFooter source="competitors + competitor_news" calc="join + agregaciones temporales" last={fmtRel(lastRun?.started_at)} />
      </TechSection>

      {/* ============== Producción de research ============== */}
      <Grid style={{ marginBottom: '1.25rem' }}>
        <Span span={7} className="mobile-full-width">
          <TechSection title="Producción de research · mensual">
            {researchByMonth.length === 0 ? (
              <Empty>Sin items aún. Esperá la próxima generación auto del cron IA.</Empty>
            ) : (
              <div style={{ height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={researchByMonth}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
                    <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0, 180, 216, 0.06)' }} />
                    <Bar dataKey="value" name="Items" fill={palette.candidato} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <SectionFooter source="research_items" calc="count(*) agrupado por mes de generated_at" last={fmtRel(data.research?.runs?.[0]?.started_at)} />
          </TechSection>
        </Span>

        <Span span={5} className="mobile-full-width">
          <TechSection title="Autores · ranking real">
            {researchAuthors.length === 0 ? (
              <Empty>Sin autores aún.</Empty>
            ) : (
              <SourceList>
                {researchAuthors.map(([name, count], i) => (
                  <SourceRow key={name}>
                    <span className="name">{name}</span>
                    <TechBar>
                      <TechBarFill
                        value={Math.round((count / researchAuthors[0][1]) * 100)}
                        color={i === 0 ? palette.candidato : palette.alt1}
                      />
                    </TechBar>
                    <span className="count">×{count}</span>
                  </SourceRow>
                ))}
              </SourceList>
            )}
            <SectionFooter source="research_items.author" calc="count(*) agrupado por autor" last={fmtRel(data.research?.runs?.[0]?.started_at)} />
          </TechSection>
        </Span>
      </Grid>

      {/* ============== Insights IA ============== */}
      <TechSection
        title="Insights IA · auto-generados semanalmente"
        action={<StatusPill>Próxima · {fmtDate(nextResearch)}</StatusPill>}
        style={{ marginBottom: '1.25rem' }}
      >
        {(data.insights?.insights || []).length === 0 ? (
          <Empty>Aún no se generaron insights. La primera corrida ocurre el próximo {fmtDate(nextResearch)}.</Empty>
        ) : (
          (data.insights.insights || []).map((ins) => (
            <InsightCard key={ins.id} $color={ins.kind === 'gaps' ? palette.alt2 : accent}>
              <div className="head">
                <FaBrain style={{ color: ins.kind === 'gaps' ? palette.alt2 : accent }} />
                <span className="kind">{ins.kind === 'gaps' ? 'Brechas detectadas' : 'Síntesis estratégica'}</span>
                <span className="ts">generado · {fmtDateTime(ins.generated_at)}</span>
              </div>
              <div className="body">
                <ReactMarkdown>{ins.body}</ReactMarkdown>
              </div>
            </InsightCard>
          ))
        )}
        <SectionFooter source="hallazgos de la investigación con IA" calc="se recalcula una vez por semana" last={fmtRel(data.insights?.insights?.[0]?.generated_at)} />
      </TechSection>

      {/* ============== Últimas noticias ============== */}
      <TechSection title={`Últimas noticias indexadas · ${Math.min(15, data.news.length)} de ${totalArticlesSeen}`}>
        {data.news.length === 0 ? (
          <Empty>Sin noticias.</Empty>
        ) : (
          <NewsList>
            {data.news.slice(0, 15).map((n) => (
              <NewsItem key={n.id}>
                <a href={n.url} target="_blank" rel="noopener noreferrer">
                  {n.title}
                  <FaExternalLinkAlt />
                </a>
                <span className="meta">
                  {n.competitor_name && (
                    <TechChip tone="mute" style={{ fontSize: '0.6rem', marginRight: 6, padding: '1px 6px' }}>
                      {n.competitor_name}
                    </TechChip>
                  )}
                  {n.source} · {fmtRel(n.published_at || n.fetched_at)}
                </span>
              </NewsItem>
            ))}
          </NewsList>
        )}
        <SectionFooter source="competitor_news" calc="últimas 15 ordenadas por fecha" last={fmtRel(lastRun?.started_at)} />
      </TechSection>
    </TechShell>
  );
};

export default KPIs;
